import React from 'react';
import { useReportUi } from './useReportUi';

const CHART_SEMANTICS = ['info', 'success', 'warning', 'danger', 'primary', 'secondary'];
const STROKE_COLORS = ['#0ea5e9', '#10b981', '#f59e0b', '#f43f5e', '#6366f1', '#94a3b8'];

/**
 * Lightweight theme-aware charts (no chart library).
 * data: [{ label, value, semantic? }]
 * type: 'bar' | 'doughnut'
 */
const ChartCard = ({ title, description, data, type = 'bar', className }) => {
  const { theme, dash, cn } = useReportUi();
  const rows = Array.isArray(data) ? data : [];
  const hasData = rows.some((item) => Number(item.value) > 0);

  const barColor = (index, item) => {
    if (item.semantic) return dash.fill(item.semantic);
    return dash.fill(CHART_SEMANTICS[index % CHART_SEMANTICS.length]);
  };

  const renderSimpleChart = () => {
    if (!hasData) {
      return (
        <div className="flex items-center justify-center h-52">
          <p className={theme.text.secondary}>No data available</p>
        </div>
      );
    }

    const maxValue = Math.max(...rows.map((item) => Number(item.value) || 0), 1);

    return (
      <div className="h-52 flex items-end justify-between gap-1 sm:gap-1.5 px-1 pt-6 pb-1">
        {rows.map((item, index) => {
          const value = Number(item.value) || 0;
          const height = (value / maxValue) * 100;
          return (
            <div
              key={`${item.label}-${index}`}
              className="flex flex-col items-center justify-end flex-1 min-w-0 h-full"
            >
              {value > 0 ? (
                <span className={cn('text-[10px] tabular-nums mb-1', theme.text.muted)}>
                  {value}
                </span>
              ) : (
                <span className="text-[10px] mb-1 opacity-0">0</span>
              )}
              <div
                className={cn(
                  'w-full max-w-[2.25rem] rounded-t transition-all duration-300',
                  value > 0 ? barColor(index, item) : 'bg-slate-200 dark:bg-slate-700',
                  value > 0 ? 'min-h-[4px]' : 'min-h-[2px]',
                )}
                style={{ height: `${Math.max(height, value > 0 ? 4 : 1)}%` }}
                title={`${item.label}: ${value}`}
              />
              <span
                className={cn(
                  'text-[10px] sm:text-xs mt-2 truncate w-full text-center',
                  theme.text.secondary,
                )}
                title={item.label}
              >
                {item.label}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  const renderDoughnutChart = () => {
    if (!hasData) {
      return (
        <div className="flex items-center justify-center h-52">
          <p className={theme.text.secondary}>No data available</p>
        </div>
      );
    }

    const total = rows.reduce((sum, item) => sum + (Number(item.value) || 0), 0);
    let currentAngle = 0;

    return (
      <div className="h-52 flex items-center justify-center relative">
        <svg width="140" height="140" className="transform -rotate-90" viewBox="0 0 140 140">
          {rows.map((item, index) => {
            const value = Number(item.value) || 0;
            if (value <= 0) return null;
            const percentage = total ? (value / total) * 100 : 0;
            const radius = 52;
            const circumference = 2 * Math.PI * radius;
            const strokeDasharray = `${(percentage / 100) * circumference} ${circumference}`;
            const startAngle = currentAngle;
            currentAngle += (percentage / 100) * 360;

            return (
              <circle
                key={`${item.label}-${index}`}
                cx="70"
                cy="70"
                r={radius}
                fill="none"
                stroke={item.color || STROKE_COLORS[index % STROKE_COLORS.length]}
                strokeWidth="22"
                strokeDasharray={strokeDasharray}
                strokeDashoffset={-((startAngle / 360) * circumference)}
                className="transition-all duration-300"
              >
                <title>{`${item.label}: ${value}`}</title>
              </circle>
            );
          })}
        </svg>
        <div className="absolute text-center">
          <div className={cn('text-xl font-semibold tabular-nums', theme.text.primary)}>{total}</div>
          <div className={cn('text-xs', theme.text.secondary)}>Total</div>
        </div>
      </div>
    );
  };

  return (
    <div className={cn(dash.widgetShell(), 'p-6', className)}>
      <h3 className={cn('font-semibold text-lg', theme.text.primary)}>{title}</h3>
      {description ? (
        <p className={cn('text-sm mt-1 mb-3', theme.text.muted)}>{description}</p>
      ) : (
        <div className="mb-3" />
      )}
      <div className="relative">{type === 'doughnut' ? renderDoughnutChart() : renderSimpleChart()}</div>
      {hasData && type === 'doughnut' ? (
        <div className="mt-4 flex flex-wrap gap-x-3 gap-y-2">
          {rows
            .filter((item) => Number(item.value) > 0)
            .map((item, index) => (
              <div key={`${item.label}-${index}`} className="flex items-center gap-2">
                <div
                  className={cn(
                    'w-3 h-3 rounded-full',
                    dash.dot(item.semantic || CHART_SEMANTICS[index % CHART_SEMANTICS.length]),
                  )}
                />
                <span className={cn('text-xs', theme.text.secondary)}>
                  {item.label}: {item.value}
                </span>
              </div>
            ))}
        </div>
      ) : null}
    </div>
  );
};

export default ChartCard;
